import { formatMoney } from './money-formatting.js';

/**
 * <wishlist-page-grid> renders the products a visitor has saved to their
 * wishlist (see wishlist.js for the underlying localStorage store).
 * Product data is fetched client-side from the public `/products/{handle}.js`
 * endpoint since the wishlist has no server-side knowledge of which products
 * are saved.
 */
class WishlistPageGrid extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.getAttribute('data-money-format') || '{{amount}}';
    this.currency = this.getAttribute('data-currency') || 'EUR';

    this._onUpdate = this._onUpdate.bind(this);
    document.addEventListener('wishlist:updated', this._onUpdate);

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this._onUpdate);
  }

  _onUpdate() {
    this.render();
  }

  async render() {
    const handles = window.Wishlist ? window.Wishlist.all() : [];

    if (handles.length === 0) {
      this._renderEmpty();
      return;
    }

    this._renderLoading();

    const results = await Promise.all(
      handles.map((handle) =>
        fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );

    // Drop handles that no longer resolve to a product (deleted/unpublished)
    // so the wishlist count stays accurate.
    const stale = handles.filter((_, index) => results[index] === null);
    if (stale.length > 0 && window.Wishlist) {
      stale.forEach((handle) => window.Wishlist.remove(handle));
      return; // remove() dispatches wishlist:updated, which will re-trigger render()
    }

    this._renderProducts(results.filter(Boolean));
  }

  _renderLoading() {
    this.innerHTML = `<div class="wishlist-page__loading">${this.getAttribute('data-loading-text') || ''}</div>`;
  }

  _renderEmpty() {
    const title = this.getAttribute('data-empty-title') || '';
    const text = this.getAttribute('data-empty-text') || '';
    this.innerHTML = `
      <div class="wishlist-page__empty">
        <h2 class="wishlist-page__empty-title">${escapeHtml(title)}</h2>
        <p class="wishlist-page__empty-text">${escapeHtml(text)}</p>
      </div>
    `;
  }

  _renderProducts(products) {
    const removeLabel = this.getAttribute('data-remove-label') || '';

    this.innerHTML = `
      <ul class="wishlist-page__list" role="list">
        ${products
          .map((product) => {
            const image = product.featured_image || (product.images && product.images[0]);
            const price = formatMoney(product.price, this.moneyFormat, this.currency);
            return `
              <li class="wishlist-page__item">
                <div class="wishlist-page__card">
                  <a href="${escapeAttr(product.url)}" class="wishlist-page__card-link">
                    <span class="wishlist-page__card-image-wrap">
                      ${
                        image
                          ? `<img src="${escapeAttr(resizeImage(image, 600))}" alt="${escapeAttr(product.title)}" loading="lazy" width="600" height="600" class="wishlist-page__card-image">`
                          : ''
                      }
                    </span>
                    <span class="wishlist-page__card-title">${escapeHtml(product.title)}</span>
                    <span class="wishlist-page__card-price">${price}</span>
                  </a>
                  <button
                    type="button"
                    class="wishlist-page__remove-button"
                    data-remove-handle="${escapeAttr(product.handle)}"
                    aria-label="${escapeAttr(removeLabel)}"
                  >
                    &times;
                  </button>
                </div>
              </li>
            `;
          })
          .join('')}
      </ul>
    `;

    this.querySelectorAll('[data-remove-handle]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const handle = button.getAttribute('data-remove-handle');
        if (handle && window.Wishlist) window.Wishlist.remove(handle);
      });
    });
  }
}

/**
 * Builds a resized Shopify CDN image URL for a given target width.
 * @param {string} url
 * @param {number} width
 */
function resizeImage(url, width) {
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}width=${width}`;
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/** @param {string} value */
function escapeAttr(value) {
  return escapeHtml(value);
}

if (!customElements.get('wishlist-page-grid')) {
  customElements.define('wishlist-page-grid', WishlistPageGrid);
}
